import { FC, useEffect, useState } from 'react';
import { Row, Col, Card, Typography } from 'antd';
import { usePermission } from 'hooks';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import {
  fetchOrganizationCharts,
  fetchUsersCharts
} from 'services/api/analytics';
import {
  OrganizationChartsResponse,
  UsersChartsResponse
} from 'types/api/analytics';

import useOrgEffect from 'hooks/useOrgEffect';
import { Loading } from 'components/ui';
import CountCard from '../CountCard';
import OrganizationChart from '../OrganizationChart';
import PieChart from '../PieChart';

const DoctorsChart: FC = () => {
  const { checkUserPermission } = usePermission();
  const [results, setResults] = useState<UsersChartsResponse | null>(null);
  const [organizationsResults, setOrganizationsResults] =
    useState<OrganizationChartsResponse | null>(null);

  const [loading, setLoading] = useState(true);
  const [organizationLoading, setOrganizationLoading] = useState(true);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchUsersCharts();
        if (!cancelled) setResults(res);
      } catch {
        // swallow
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetchOrganizationCharts();
        if (!cancelled) setOrganizationsResults(res);
      } catch {
        // swallow
      } finally {
        if (!cancelled) setOrganizationLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const hasDoctorReadPermission = checkUserPermission(
    PERMISSION_ROUTES.doctors,
    PERMISSION_ACTIONS.read
  );

  if (loading || organizationLoading || !results) {
    return <Loading />;
  }

  return (
    <>
      <Row gutter={[16, 24]} style={{ marginBottom: 24 }}>
        <Col lg={{ span: 16 }}>
          <Card
            bodyStyle={{ padding: '20px 24px 16px' }}
            style={{ height: '100%' }}
          >
            <Typography.Title level={4} style={{ marginBottom: 20 }}>
              Doctors from organizations
            </Typography.Title>
            <OrganizationChart
              data={Object.entries(
                organizationsResults?.organization_count || {}
              )?.map(([key, value]) => {
                return {
                  country: key,
                  value: (value as number) || 0
                };
              })}
            />
          </Card>
        </Col>
        <Col lg={{ span: 8 }}>
          <Card
            bodyStyle={{
              padding: '20px 24px 16px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
            style={{ height: '100%' }}
          >
            <CountCard
              title="Total doctors"
              to={hasDoctorReadPermission ? '/access/doctors' : null}
              count={results.doctors?.total || 0}
            >
              <PieChart
                id="doctorsPieChart"
                data={[
                  {
                    status: `Active`,
                    value: results.doctors?.active || 0,
                    color: '#4F84E8'
                  },
                  {
                    status: `Inactive`,
                    value: results.doctors?.disabled || 0,
                    color: '#C6C9D1'
                  }
                ]}
              />
            </CountCard>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default DoctorsChart;
